import React, { useEffect, useState } from "react";
import config from "../../config";

const StockVisePayment = ({ showModal, closeModal, onSave, selectedStockPayments, supplierName, supplierId }) => {
    const [paymentType, setPaymentType] = useState('cash');
    const [chequeDetails, setChequeDetails] = useState([]);
    const [payments, setPayments] = useState([]);
    const [totalChequeAmount, setTotalChequeAmount] = useState(0);

    useEffect(() => {
        if (selectedStockPayments) {
            const initialPayments = selectedStockPayments.map(payment => ({
                stockPaymentId: payment.stockPaymentId,
                stockName: payment.stockName,
                total: payment.total,
                due: payment.due,
                payingAmount: 0
            }));
            setPayments(initialPayments);
        }
    }, [selectedStockPayments]);

    useEffect(() => {
        const total = chequeDetails.reduce((sum, cheque) => sum + Number(cheque.chequeAmounts || 0), 0);
        setTotalChequeAmount(total);

        if (paymentType === 'cheque') {
            distributePaymentAmount(total);
        }
    }, [chequeDetails, paymentType]);

    const distributePaymentAmount = (totalAmount) => {
        if (payments.length === 0 || totalAmount <= 0) {
            return;
        }

        let newPayments = [...payments];
        let remainingAmount = totalAmount;

        newPayments = newPayments.map(payment => ({
            ...payment,
            payingAmount: 0
        }));

        for (let i = 0; i < newPayments.length && remainingAmount > 0; i++) {
            const payment = newPayments[i];
            const amountToApply = Math.min(payment.due, remainingAmount);

            newPayments[i] = {
                ...payment,
                payingAmount: amountToApply
            };

            remainingAmount -= amountToApply;
        }

        setPayments(newPayments);
    };

    const handleAddChequeField = () => {
        setChequeDetails((prevDetails) => [
            ...prevDetails,
            { chequeNumber: '', chequeAmounts: '', chequeDate: '' },
        ]);
    };

    const handleDynamicFieldChange = (index, name, value) => {
        setChequeDetails((prevDetails) => {
            const updatedDetails = [...prevDetails];
            updatedDetails[index][name] = value;
            return updatedDetails;
        });
    };

    const handlePaymentChange = (index, value) => {
        if (paymentType === 'cheque') {
            alert("When using cheque payment, the amounts are automatically calculated from cheque values.");
            return;
        }

        const newPayments = [...payments];
        newPayments[index].payingAmount = Number(value);
        setPayments(newPayments);
    };

    const getSriLankanTime = () => {
        return new Date().toISOString();
    };

    const handlePaymentTypeChange = (type) => {
        setPaymentType(type);

        if (type === 'cash') {
            setPayments(prev => prev.map(payment => ({
                ...payment,
                payingAmount: 0
            })));
        } else if (type === 'cheque') {
            distributePaymentAmount(totalChequeAmount);
        }
    };

    const handleSubmit = async () => {
        try {
            if (paymentType === 'cheque') {
                const totalPayingAmount = payments.reduce((sum, payment) => sum + Number(payment.payingAmount), 0);

                if (Math.abs(totalChequeAmount - totalPayingAmount) > 0.01) {
                    alert(`Error: Total cheque amount (${totalChequeAmount}) must match total payment amount (${totalPayingAmount})`);
                    return;
                }

                for (const cheque of chequeDetails) {
                    if (!cheque.chequeNumber || !cheque.chequeAmounts || !cheque.chequeDate) {
                        alert("Please fill in all cheque details (number, amount, date)");
                        return;
                    }
                }
            }

            // Check if any payment is being made
            const totalPayingAmount = payments.reduce((sum, payment) => sum + Number(payment.payingAmount), 0);
            if (totalPayingAmount <= 0) {
                alert("Please enter a payment amount greater than zero");
                return;
            }

            // Process each payment
            await Promise.all(payments.map(async (payment) => {
                if (payment.payingAmount > 0) {
                    const newDue = payment.due - payment.payingAmount;
                    const cashAmount = paymentType === 'cash' ? payment.payingAmount : 0;
                    const chequeAmount = paymentType === 'cheque' ? payment.payingAmount : 0;

                    await fetch(`${config.BASE_URL}/stockPayment/${payment.stockPaymentId}`, {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            cashAmount,
                            chequeAmount,
                            due: newDue,
                            total: payment.total,
                            stockPaymentStatus: newDue === 0 ? "Paid" : "Unpaid",
                            supplierId: supplierId
                        })
                    });
                }
            }));

            // Handle cheque details if needed
            if (paymentType === 'cheque' && chequeDetails.length > 0) {
                if (!supplierId) {
                    throw new Error("Supplier ID is missing.");
                }

                const lastStockPaymentId = payments[payments.length - 1]?.stockPaymentId;

                if (!lastStockPaymentId) {
                    throw new Error("Stock Payment ID is missing.");
                }

                const chequeResponse = await fetch(`${config.BASE_URL}/cheque`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(
                        chequeDetails.map((cheque) => ({
                            chequeNumber: cheque.chequeNumber,
                            chequeAmount: cheque.chequeAmounts,
                            issuedDate: getSriLankanTime(),
                            chequeDate: cheque.chequeDate,
                            supplierId,
                            stockPaymentId: lastStockPaymentId,
                        }))
                    ),
                });

                if (!chequeResponse.ok) {
                    const error = await chequeResponse.json();
                    console.error('Error details:', error);
                    throw new Error(error.error || 'Failed to add cheque details.');
                }

                const chequeData = await chequeResponse.json();
                console.log('Cheques Added:', chequeData);
            }

            // Create supplier payment with correct payment type
            const supplierPaymentResponse = await fetch(`${config.BASE_URL}/supplierPayment`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    payDate: getSriLankanTime(),
                    payAmount: totalPayingAmount,
                    paymentType: paymentType,
                    stockPaymentId: payments[0]?.stockPaymentId,
                })
            });

            if (!supplierPaymentResponse.ok) {
                const error = await supplierPaymentResponse.json();
                console.error('Error details:', error);
                throw new Error(error.error || 'Failed to add supplier payment.');
            }

            const supplierPaymentData = await supplierPaymentResponse.json();
            console.log('Supplier Payment Added:', supplierPaymentData);

            onSave();
            closeModal();
        } catch (err) {
            console.error('Payment error:', err);
            alert(`Error: ${err.message || 'Failed to process payment'}`);
        }
    };

    if (!showModal) return null;

    const totalDueAmount = payments.reduce((sum, payment) => sum + payment.due, 0);
    const totalPayingAmount = payments.reduce((sum, payment) => sum + Number(payment.payingAmount), 0);

    return (

        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(0, 0, 0, 0.6)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 1000, backdropFilter: "blur(2px)" }}>
            <div style={{ background: "#ffffff", borderRadius: "12px", boxShadow: "0 8px 32px rgba(0, 0, 0, 0.2)", width: "90%", maxWidth: "600px", maxHeight: "90vh", overflowY: "auto" }}>

                <div style={{ padding: "1.5rem", borderBottom: "1px solid #e9ecef", backgroundColor: "#f8f9fa", display: "flex", justifyContent: "space-between", alignItems: "center" }}>

                    <h5 style={{ fontSize: "1.4rem", fontWeight: "600", color: "#2c3e50", margin: 0 }}>
                        <i className="bi bi-wallet2" style={{ marginRight: "0.5rem" }}></i>
                        Payment for {supplierName}
                    </h5>

                    <button
                        type="button"
                        onClick={closeModal}
                        style={{ background: "none", border: "none", fontSize: "1.2rem", cursor: "pointer" }}  >
                        &times;
                    </button>

                </div>

                <div style={{ padding: "1.5rem", overflowY: "auto" }}>
                    <div style={{ backgroundColor: "#f8f9fa", borderRadius: "8px", padding: "1rem", border: "1px solid #e9ecef", marginBottom: "1.5rem" }}>

                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <div>
                                <span style={{ color: "#6c757d", fontSize: "0.9rem" }}>Total Balance:</span>
                                <h3 style={{ color: "#e74c3c", fontWeight: "700", margin: "0.5rem 0 0 0" }}>
                                    LKR {totalDueAmount.toFixed(2)}
                                </h3>
                            </div>
                            <div style={{ textAlign: "right" }}>
                                <span style={{ color: "#6c757d", fontSize: "0.9rem" }}>Total Payment:</span>
                                <h3 style={{ color: "#27ae60", fontWeight: "700", margin: "0.5rem 0 0 0" }}>
                                    LKR {totalPayingAmount.toFixed(2)}
                                </h3>
                            </div>
                        </div>

                    </div>

                    <div style={{ marginBottom: "1.5rem" }}>
                        <label style={{ fontWeight: "600", color: "#6c757d", marginBottom: "0.5rem", display: "block" }}>
                            SELECT PAYMENT METHOD
                        </label>

                        <div style={{ display: "flex", gap: "0.5rem" }}>
                            <button
                                style={{
                                    flex: 1,
                                    padding: "0.8rem 1rem",
                                    border: "1px solid #dee2e6",
                                    borderRadius: "8px",
                                    background: paymentType === 'cash' ? "#3498db" : "#ffffff",
                                    color: paymentType === 'cash' ? "white" : "#6c757d",
                                    transition: "all 0.2s",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    cursor: "pointer"
                                }}
                                onClick={() => handlePaymentTypeChange('cash')}
                            >
                                <i className="bi bi-cash" style={{ marginRight: "0.5rem" }}></i>
                                Cash Payment
                            </button>

                            <button
                                style={{
                                    flex: 1,
                                    padding: "0.8rem 1rem",
                                    border: "1px solid #dee2e6",
                                    borderRadius: "8px",
                                    background: paymentType === 'cheque' ? "#3498db" : "#ffffff",
                                    color: paymentType === 'cheque' ? "white" : "#6c757d",
                                    transition: "all 0.2s",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    cursor: "pointer"
                                }}
                                onClick={() => handlePaymentTypeChange('cheque')}
                            >
                                <i className="bi bi-wallet2" style={{ marginRight: "0.5rem" }}></i>
                                Cheque Payment
                            </button>
                        </div>

                    </div>

                    {paymentType === 'cheque' && (
                        <div style={{ marginBottom: "1.5rem" }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>

                                <div>
                                    <span style={{ color: "#6c757d", fontSize: "0.9rem" }}>Total Cheque Amount:</span>
                                    <h4 style={{ color: "#2980b9", margin: "0.5rem 0 0 0" }}>LKR {totalChequeAmount.toFixed(2)}</h4>
                                </div>

                                <button
                                    style={{ background: "#3498db", color: "white", border: "none", borderRadius: "6px", padding: "0.5rem 1rem", cursor: "pointer", display: "flex", alignItems: "center", gap: "0.5rem" }}
                                    onClick={handleAddChequeField}
                                >
                                    <i className="bi bi-plus-circle"></i>
                                    Add New Cheque
                                </button>

                            </div>

                            {chequeDetails.map((detail, index) => (
                                <div key={index} style={{ backgroundColor: "#f8f9fa", borderRadius: "8px", padding: "1rem", border: "1px solid #e9ecef", marginBottom: "1rem" }}>
                                    <div style={{ display: "flex", gap: "1rem" }}>
                                        <div style={{ flex: 1 }}>
                                            <label style={{ color: "#6c757d", fontSize: "0.9rem" }}>Cheque Number</label>
                                            <input
                                                type="text"
                                                style={{ width: "100%", padding: "0.5rem", borderRadius: "6px", border: "1px solid #dee2e6" }}
                                                placeholder="Cheque Number"
                                                value={detail.chequeNumber}
                                                onChange={(e) => handleDynamicFieldChange(index, 'chequeNumber', e.target.value)}
                                            />
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            <label style={{ color: "#6c757d", fontSize: "0.9rem" }}>Amount</label>
                                            <input
                                                type="number"
                                                style={{ width: "100%", padding: "0.5rem", borderRadius: "6px", border: "1px solid #dee2e6" }}
                                                placeholder="Amount"
                                                value={detail.chequeAmounts}
                                                onChange={(e) => handleDynamicFieldChange(index, 'chequeAmounts', e.target.value)}
                                            />
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            <label style={{ color: "#6c757d", fontSize: "0.9rem" }}>Cheque Date</label>
                                            <input
                                                type="date"
                                                style={{ width: "100%", padding: "0.5rem", borderRadius: "6px", border: "1px solid #dee2e6" }}
                                                value={detail.chequeDate}
                                                onChange={(e) => handleDynamicFieldChange(index, 'chequeDate', e.target.value)}
                                            />
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    <div>
                        {payments.map((payment, index) => (
                            <div key={index} style={{ backgroundColor: "#ffffff", borderRadius: "8px", padding: "1rem", border: "1px solid #e9ecef", marginBottom: "1rem" }}>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem" }}>

                                    <div>
                                        <span style={{ color: "#6c757d", fontSize: "0.9rem" }}>Stock Name</span>
                                        <h6 style={{ fontWeight: "600", margin: "0.5rem 0 0 0" }}>{payment.stockName}</h6>
                                    </div>

                                    <span style={{ background: "#ffc107", color: "#000", padding: "0.25rem 0.5rem", borderRadius: "4px", fontSize: "0.9rem" }}>
                                        Balance: LKR {payment.due.toFixed(2)}
                                    </span>
                                </div>

                                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                                    <span style={{ background: "#e9ecef", padding: "0.5rem", borderRadius: "6px" }}>LKR</span>
                                    <input
                                        type="number"
                                        style={{
                                            flex: 1,
                                            padding: "0.5rem",
                                            borderRadius: "6px",
                                            border: "1px solid #dee2e6",
                                            backgroundColor: paymentType === 'cheque' ? "#f8f9fa" : "#ffffff",
                                            cursor: paymentType === 'cheque' ? "not-allowed" : "auto"
                                        }}
                                        placeholder="Enter amount"
                                        value={payment.payingAmount}
                                        onChange={(e) => handlePaymentChange(index, e.target.value)}
                                        readOnly={paymentType === 'cheque'}
                                    />
                                </div>

                            </div>
                        ))}
                    </div>
                </div>

                <div style={{ padding: "1rem 1.5rem", borderTop: "1px solid #e9ecef", backgroundColor: "#f8f9fa", display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}>

                    <button
                        style={{ background: "#6c757d", color: "white", border: "none", borderRadius: "6px", padding: "0.5rem 1rem", cursor: "pointer", minWidth: "80px" }}
                        onClick={closeModal}
                    >
                        Cancel
                    </button>

                    <button
                        style={{ background: "#28a745", color: "white", border: "none", borderRadius: "6px", padding: "0.5rem 1rem", cursor: "pointer", minWidth: "120px", display: "flex", alignItems: "center", gap: "0.5rem" }}
                        onClick={handleSubmit}
                    >
                        <i className="bi bi-check2-circle"></i>
                        Confirm Payment
                    </button>

                </div>

            </div>
        </div>
    );
};

export default StockVisePayment;